<script lang="ts" setup>
import dayjs from 'dayjs';

// ================================
// Composables
// ================================
const { areas } = storeToRefs(areaStore());
const { formatNumberToIsoNumber } = useNumbers();

// ================================
// Props
// ================================
const props = defineProps<{
  performanceLoading: boolean;
  id: number;
  isDraft: boolean;
  baselineData: {
    areaId: number;
    variables: string[];
    overallR2: number | null;
    last3MonthsR2: number | null;
  };
  performanceData: {
    latestPerformanceDate: string;
    actualPerformance: number;
    predictedPerformance: number;
    energySaved: {
      value: number;
      isPositive: boolean;
    };
  } | null;
}>();

// ================================
// Computed
// ================================
const title = computed(() => {
  return areas.value.find((area) => area.id === props.baselineData.areaId)?.name;
});

// ================================
// Methods
// ================================
const handleClick = () => {
  if (props.isDraft) {
    navigateTo({ name: 'baseline-edit-training-period', params: { id: props.id.toString() } });
  } else {
    navigateTo({ name: 'baseline-view', params: { id: props.id.toString() } });
  }
};
</script>
<template>
  <div
    class="bg-white rounded-lg border border-gray-200 cursor-pointer shadow-xs hover:shadow transition-shadow duration-300"
    @click="handleClick"
  >
    <div class="p-4 border-gray-200 border-b bg-white rounded-t-lg flex items-center justify-between">
      <div class="flex items-center justify-start">
        <p class="font-semibold text-gray-700">{{ title }}</p>
      </div>
      <u-badge v-if="isDraft" color="neutral" variant="subtle">{{ $t('baseline.draft') }}</u-badge>
    </div>

    <!-- Body -->
    <div class="p-3 bg-white rounded-b-lg">
      <div class="grid grid-cols-5 gap-1 gap-y-4 border-b border-gray-200 pb-4">
        <!-- Variables -->
        <div class="col-span-2 flex items-center text-sm text-gray-500">{{ $t('baseline.variables') }}</div>
        <div class="col-span-3 flex items-center gap-2 pl-4">
          <u-badge v-for="variable in baselineData.variables" :key="variable" color="neutral" variant="subtle">{{ variable }}</u-badge>
          <u-badge v-if="baselineData.variables.length === 0" color="neutral" variant="subtle">{{ $t('baseline.no_variables') }}</u-badge>
        </div>

        <!-- Overall R2 -->
        <div class="col-span-2 flex items-center text-sm text-gray-500">{{ $t('baseline.overall_r2') }}</div>
        <div
          v-if="baselineData.overallR2"
          class="col-span-3 text-xs flex items-center pl-4"
          :class="baselineData.overallR2 > 0.5 ? 'text-green-500' : 'text-red-500'"
        >
          {{ formatNumberToIsoNumber(baselineData.overallR2, 2) }}
        </div>
        <div v-else class="col-span-3 text-xs flex items-center pl-4">
          <p class="text-gray-400 text-xs text-center">{{ $t('global.no_data') }}</p>
        </div>

        <!-- Last 3 months R2 -->
        <div class="col-span-2 flex items-center text-sm text-gray-500">{{ $t('baseline.last_3_months_r2') }}</div>
        <div
          v-if="baselineData.last3MonthsR2"
          class="col-span-3 text-xs flex items-center pl-4"
          :class="baselineData.last3MonthsR2 > 0.5 ? 'text-green-500' : 'text-red-500'"
        >
          {{ formatNumberToIsoNumber(baselineData.last3MonthsR2, 2) }}
        </div>
        <div v-else class="col-span-3 text-xs flex items-center pl-4">
          <p class="text-gray-400 text-xs text-center">{{ $t('global.no_data') }}</p>
        </div>
      </div>

      <!-- Latest Performance -->
      <div class="grid grid-cols-5 gap-1 gap-y-4 pb-4 pt-3">
        <div class="col-span-2 flex items-center text-xs text-gray-500">{{ $t('baseline.latest_performance') }}</div>
        <div v-if="performanceLoading" class="col-span-3 flex items-center justify-start pl-4">
          <ui-skeleton-loader class="w-[60%] h-3 rounded-full" />
        </div>
        <div v-else-if="performanceData" class="col-span-3 text-xs flex items-center pl-4">
          {{ dayjs(performanceData.latestPerformanceDate).format('DD/MM/YYYY') }}
        </div>
        <div v-else class="col-span-3 text-xs flex items-center pl-4">
          <p class="text-gray-400 text-xs text-center">{{ $t('global.no_data') }}</p>
        </div>
      </div>

      <!-- Performance actual vs predicted -->
      <div class="grid grid-cols-2 gap-2">
        <div class="border border-gray-200 bg-white rounded p-1 flex flex-col items-center justify-center">
          <p class="text-gray-400 text-xs text-center">{{ $t('baseline.actual') }}</p>
          <ui-skeleton-loader v-if="performanceLoading" class="w-[60%] h-3 rounded-full" />
          <p v-else-if="performanceData" class="text-gray-700 text-sm font-semibold text-center">
            {{ formatNumberToIsoNumber(performanceData.actualPerformance ?? 0) }} kWh
          </p>
          <p v-else class="text-gray-400 text-xs text-center">{{ $t('global.no_data') }}</p>
        </div>
        <div class="border border-gray-200 bg-white rounded p-1 flex flex-col items-center justify-center">
          <p class="text-gray-400 text-xs text-center">{{ $t('baseline.predicted') }}</p>
          <ui-skeleton-loader v-if="performanceLoading" class="w-[60%] h-3 rounded-full" />
          <p v-else-if="performanceData" class="text-gray-700 text-sm font-semibold text-center">
            {{ formatNumberToIsoNumber(performanceData.predictedPerformance ?? 0) }} kWh
          </p>
          <p v-else class="text-gray-400 text-xs text-center">{{ $t('global.no_data') }}</p>
        </div>
      </div>

      <!-- Pourcent of energy saved -->
      <div class="flex items-center justify-center pt-3">
        <ui-skeleton-loader v-if="performanceLoading" class="w-[60%] h-3 rounded-full" />
        <p
          v-else-if="performanceData"
          class="text-xs lowercase"
          :class="performanceData.energySaved.isPositive ? 'text-green-500' : 'text-red-500'"
        >
          {{ performanceData.energySaved.value }} %
          {{ performanceData.energySaved.isPositive ? $t('baseline.saving') : $t('baseline.excess') }}
        </p>
        <p v-else class="text-gray-400 text-xs text-center">{{ $t('global.no_data') }}</p>
      </div>
    </div>
  </div>
</template>
